// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Loader2, Package } from 'lucide-react';
import PackageManagementModal from '@/components/vendor-video/PackageManagementModal';

interface VideoPackage {
  id: string;
  vendor_id: string;
  package_type: string;
  price_cents: number;
  description?: string;
  features?: string[];
  is_active: boolean;
  created_at: string;
}

interface VideoVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
}

const VendorPackages: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<VideoPackage[]>([]);
  const [vendorProfile, setVendorProfile] = useState<VideoVendorProfile | null>(null);
  const [showPackageModal, setShowPackageModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;

    try {
      // Get vendor profile
      const { data: profileData, error: profileError } = await supabase
        .from('video_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setVendorProfile(profileData);

      // Get packages if we have a vendor profile
      if (profileData?.id) {
        const { data: packagesData, error: packagesError } = await supabase
          .from('video_packages')
          .select('*')
          .eq('vendor_id', profileData.id)
          .order('created_at', { ascending: false });

        if (packagesError) throw packagesError;
        setPackages(packagesData || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading packages...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Service Packages</h1>
          <p className="text-muted-foreground">Manage your video service packages and pricing</p>
        </div>
        <Button
          onClick={() => setShowPackageModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Package
        </Button>
      </div>

      <div className="grid gap-6">
        {packages.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No packages yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Create your first service package to start offering video services
              </p>
              <Button onClick={() => setShowPackageModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Package
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="relative">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{pkg.package_type}</CardTitle>
                    <Badge variant={pkg.is_active ? "default" : "secondary"}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-2xl font-bold text-primary">
                    {formatPrice(pkg.price_cents)}
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {pkg.description || 'No description'}
                  </p>
                  
                  {pkg.features && pkg.features.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2">Features:</h4>
                      <ul className="text-sm space-y-1">
                        {pkg.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-1">
                            <span className="text-primary mt-1">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {vendorProfile && (
        <PackageManagementModal
          open={showPackageModal}
          onOpenChange={setShowPackageModal}
          vendorId={vendorProfile.id}
          onPackageUpdate={fetchData}
        />
      )}
    </div>
  );
};

export default VendorPackages;